import React, { useState, useEffect, useCallback } from 'react';
import {
  UserCircle,
  LogOut,
  ChevronRight,
  Users,
  ClipboardList,
  ClipboardCheck,
  Briefcase,
  Headphones,
  FileInput,
  UserCheck,
  UserCog,
  Calendar,
  CalendarClock,
  Calculator,
  GitBranch,
  FileText,
  FileCheck,
  FileEdit,
  Shield,
  TrendingUp,
  PhoneCall,
  Target,
  FolderOpen,
  Settings,
  MessagesSquare,
  FileCode,
  History,
  FileSignature,
  Send,
  UserX,
  LayoutDashboard,
  Sun,
  Moon,
  Palette,
  Paintbrush,
  TextQuote,
  LayoutList,
  Files,
  StickyNote,
  Map,
  LayoutGrid,
  FlaskConical,
  Package,
  Table2,
  ListTodo,
  MessageSquare,
  Phone,
  BookOpen,
  HelpCircle,
  LifeBuoy,
  BarChart3,
  PieChart,
  LineChart,
  ListOrdered,
  Eye,
  EyeOff,
  Sparkles,
  Mail,
  FileSpreadsheet,
  Megaphone,
  Upload,
  Braces,
  Rocket,
  SlidersHorizontal,
  Shuffle,
  Receipt,
  Workflow,
  MessageCircle,
  X,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  useSidebar,
} from './ui/sidebar.tsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible.tsx';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx';
import { useAppHeader } from '../contexts/AppHeaderContext';
import { useTheme, THEMES, COLOR_THEMES } from '../contexts/ThemeContext';
import SidebarColourPicker from './SidebarColourPicker';
import userTeamService from '../services/userTeamService';
import { NAV_GROUP_SIDEBAR_ORDER, SIDEBAR_ORDER_BY_GROUP, VIEW_AS_ROLE_LABELS } from '../config/navConfig';
import {
  loadSidebarGroupVisibility,
  saveSidebarGroupVisibility,
  isSidebarGroupExpanded,
} from '../utils/sidebarGroupVisibility';
import { cn } from '../lib/utils';
import { isNewTabClick } from '../contexts/WorkspaceTabsContext';
import { requestOpenSupportChat, subscribeSupportChatUnread } from '../services/supportChatBus';

function useSupportChatUnread(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => (enabled ? subscribeSupportChatUnread(setCount) : undefined), [enabled]);
  return enabled ? count : 0;
}

const formatBadgeCount = (n) => (n > 99 ? '99+' : n);

const COLOR_THEME_LABELS = {
  [COLOR_THEMES.ocean]: 'Ocean',
  [COLOR_THEMES.slate]: 'Slate',
  [COLOR_THEMES.paper]: 'Paper',
  [COLOR_THEMES.mist]: 'Mist',
  [COLOR_THEMES.midnight]: 'Midnight',
  [COLOR_THEMES.blush]: 'Blush',
  [COLOR_THEMES.amber]: 'Amber',
  [COLOR_THEMES.bloom]: 'Bloom',
  [COLOR_THEMES.win95]: 'Windows 95',
};

const ICON_MAP = {
  Users,
  MessagesSquare,
  LayoutDashboard,
  Target,
  ClipboardList,
  ClipboardCheck,
  Briefcase,
  Headphones,
  FileInput,
  UserCheck,
  UserCog,
  Calendar,
  CalendarClock,
  Calculator,
  GitBranch,
  FileText,
  FileCheck,
  FileEdit,
  Shield,
  TrendingUp,
  PhoneCall,
  FolderOpen,
  Settings,
  FileCode,
  History,
  FileSignature,
  Send,
  UserX,
  StickyNote,
  Map,
  LayoutGrid,
  FlaskConical,
  Package,
  Table2,
  ListTodo,
  MessageSquare,
  Phone,
  BookOpen,
  HelpCircle,
  LifeBuoy,
  BarChart3,
  PieChart,
  LineChart,
  ListOrdered,
  Eye,
  EyeOff,
  Sparkles,
  Mail,
  FileSpreadsheet,
  Megaphone,
  Upload,
  Braces,
  Rocket,
  SlidersHorizontal,
  Receipt,
  Palette,
  Paintbrush,
  TextQuote,
  LayoutList,
  Files,
  Workflow,
  Shuffle,
};

const ROLES_NAV_ITEMS = [
  { key: 'ga', label: VIEW_AS_ROLE_LABELS.ga, icon: Users },
  { key: 'paraplanner', label: VIEW_AS_ROLE_LABELS.paraplanner, icon: FileText },
  { key: 'paraplannerManagement', label: VIEW_AS_ROLE_LABELS.paraplannerManagement, icon: Shield },
  { key: 'adviser', label: VIEW_AS_ROLE_LABELS.adviser, icon: Briefcase },
  { key: 'management', label: VIEW_AS_ROLE_LABELS.management, icon: Shield },
  { key: 'ufSupport', label: VIEW_AS_ROLE_LABELS.ufSupport, icon: Headphones },
  { key: 'arSupport', label: VIEW_AS_ROLE_LABELS.arSupport, icon: Headphones },
  { key: 'arAdviser', label: VIEW_AS_ROLE_LABELS.arAdviser, icon: UserCheck },
  { key: 'compliance', label: VIEW_AS_ROLE_LABELS.compliance, icon: Shield },
];

function formatRoleLabel(role) {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * App sidebar (sidebar-08 style): collapsible top-level pages with optional section sub-items.
 * Use inside SidebarProvider. Receives nav tree from getNavTreeForUser(accessibleTabs).
 * When viewAsRole is set, admin is viewing as another role (no Roles section). When viewAsRole is null and user is super_admin, show Roles section.
 */
export function AppSidebar({
  navTree = [],
  activeTab = null,
  activeSection = null,
  onNavigate,
  onLogoClick,
  tabDisabledNoClient = () => false,
  viewAsRole = null,
  onViewAsRole,
  /** Optional unread counts by tabName (e.g. { email: 3 }); leaf items > 0 show a badge. */
  badgesByTab = {},
  /** Optional tooltip text for a badge by tabName (what the count is made of). */
  badgeHintsByTab = {},
  /** Cmd/Ctrl-click or middle-click on a page: (tabName, sectionId) opens it in a new workspace tab. */
  onOpenInNewTab,
  /** Show the Support chat entry in the footer. */
  showSupportChat = false,
  /** Exits view-as mode (shown in the footer while viewAsRole is set). */
  onExitViewAs,
  /** Selected client's name: labels the Client group ("Client" when none). */
  clientGroupLabel = null,
  /** First settings page this user can open (null hides the Settings entry). */
  settingsTab = null,
  /** True while any settings page is open (highlights the Settings entry). */
  settingsActive = false,
  ...props
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  // On phones the sidebar is a sheet over the page: close it once a page is picked.
  const closeOnPhone = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
  const navigate = useCallback((...args) => {
    onNavigate?.(...args);
    closeOnPhone();
  }, [onNavigate, closeOnPhone]);
  const header = useAppHeader();
  const hasUser = header?.user != null;
  const supportChatUnread = useSupportChatUnread(showSupportChat);
  // Cmd/Ctrl-click or middle-click opens the page in a new workspace tab.
  const navigateFromClick = useCallback((event, tabName, sectionId) => {
    if (onOpenInNewTab && isNewTabClick(event)) {
      event.preventDefault();
      onOpenInNewTab(tabName, sectionId);
      closeOnPhone();
      return;
    }
    navigate(tabName, sectionId);
  }, [onOpenInNewTab, navigate, closeOnPhone]);
  const auxClickOpensNewTab = (tabName, sectionId) => (event) => {
    if (event.button !== 1 || !onOpenInNewTab) return;
    event.preventDefault();
    onOpenInNewTab(tabName, sectionId);
  };

  const role = header?.user?.email ? userTeamService.getUserRole(header.user.email) : null;
  const userName = header?.user?.email ? userTeamService.getUserName(header.user.email) : '';
  const roleLabel = formatRoleLabel(role);
  const userDisplay = roleLabel ? `${roleLabel} - ${userName}` : userName;
  const { theme, colorTheme, setTheme, setColorTheme } = useTheme();

  const userEmail = header?.user?.email || '';
  const [groupVisibility, setGroupVisibility] = useState(() => loadSidebarGroupVisibility(userEmail));

  useEffect(() => {
    setGroupVisibility(loadSidebarGroupVisibility(userEmail));
  }, [userEmail]);

  const toggleGroupVisibility = useCallback((groupKey) => {
    setGroupVisibility((prev) => {
      const next = {
        ...prev,
        [groupKey]: !isSidebarGroupExpanded(prev, groupKey),
      };
      saveSidebarGroupVisibility(userEmail, next);
      return next;
    });
  }, [userEmail]);

  // Submenu open state. Navigating to a page opens its submenu; the chevron can then
  // collapse/expand any submenu on its own without leaving the page. Overrides reset
  // whenever the active page changes so navigation always wins.
  const [submenuOverrides, setSubmenuOverrides] = useState({});
  useEffect(() => {
    setSubmenuOverrides({});
  }, [activeTab]);

  if (navTree.length === 0) {
    return null;
  }

  const sortByGroupOrder = (items, order) =>
    order.map((tabName) => items.find((i) => i.tabName === tabName)).filter(Boolean);
  // Groups render in NAV_GROUP_SIDEBAR_ORDER (the one source, shared with Page
  // Visibility). Settings pages are not listed: one "Settings" entry opens the hub.
  const groupItems = Object.fromEntries(NAV_GROUP_SIDEBAR_ORDER.map((group) => [
    group,
    sortByGroupOrder(navTree.filter((item) => item.navGroup === group), SIDEBAR_ORDER_BY_GROUP[group] || []),
  ]));

  const renderNavItems = (items) =>
    items.map((item) => {
      const IconComponent = ICON_MAP[item.icon] || FileText;
      const disabled = tabDisabledNoClient(item.tabName);
      const isActive = activeTab === item.tabName && !item.sections?.length;
      const hasSections = item.sections && item.sections.length > 0;

      const badgeHint = badgesByTab?.[item.tabName] > 0 ? badgeHintsByTab?.[item.tabName] : null;
      const button = (
        <SidebarMenuButton
          isActive={isActive}
          disabled={disabled}
          onClick={(e) => !disabled && navigateFromClick(e, item.tabName)}
          onAuxClick={disabled ? undefined : auxClickOpensNewTab(item.tabName)}
          tooltip={disabled ? 'Select a client first' : (badgeHint ? `${item.label} — ${badgeHint}` : item.label)}
        >
          <IconComponent className="h-4 w-4 shrink-0" />
          <span>{item.label}</span>
        </SidebarMenuButton>
      );

      if (!hasSections) {
        const badgeCount = Number(badgesByTab?.[item.tabName]) || 0;
        return (
          <SidebarMenuItem key={item.tabName}>
            {disabled ? (
              <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">Select a client first</TooltipContent>
              </Tooltip>
            ) : (
              button
            )}
            {badgeCount > 0 ? (
              <SidebarMenuBadge title={badgeHint || undefined}>{formatBadgeCount(badgeCount)}</SidebarMenuBadge>
            ) : null}
          </SidebarMenuItem>
        );
      }

      return (
        <Collapsible
          key={item.tabName}
          asChild
          className="group/collapsible"
          open={submenuOverrides[item.tabName] ?? activeTab === item.tabName}
          onOpenChange={(next) => {
            setSubmenuOverrides((prev) => ({ ...prev, [item.tabName]: next }));
            // Opening a page's submenu from elsewhere also takes you to that page;
            // collapsing never navigates away.
            if (next && activeTab !== item.tabName) {
              navigate(item.tabName, item.sections[0]?.id);
            }
          }}
        >
          <SidebarMenuItem>
            {disabled ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    isActive={activeTab === item.tabName}
                    disabled
                    tooltip="Select a client first"
                  >
                    <IconComponent className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">Select a client first</TooltipContent>
              </Tooltip>
            ) : (
              <>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    isActive={activeTab === item.tabName}
                    tooltip={item.label}
                  >
                    <IconComponent className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                    <ChevronRight
                      aria-hidden="true"
                      className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.sections.map((section) => (
                      <SidebarMenuSubItem key={section.id}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={activeTab === item.tabName && activeSection === section.id}
                        >
                          <button
                            type="button"
                            onClick={(e) => navigateFromClick(e, item.tabName, section.id)}
                            onAuxClick={auxClickOpensNewTab(item.tabName, section.id)}
                          >
                            <span>{section.title}</span>
                          </button>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </>
            )}
          </SidebarMenuItem>
        </Collapsible>
      );
    });

  const renderNavGroup = (groupKey, label, items, { extraContent = null, labelTitle, hint = null } = {}) => {
    if (!items?.length && !extraContent) return null;
    const expanded = isSidebarGroupExpanded(groupVisibility, groupKey);
    const groupToneClass = expanded
      ? 'text-sidebar-foreground/70'
      : 'text-sidebar-foreground/40';
    return (
      <SidebarGroup key={groupKey}>
        <SidebarGroupLabel className={cn('transition-colors', groupToneClass)} title={labelTitle}>
          <span className="truncate">{label}</span>
        </SidebarGroupLabel>
        {hint && expanded ? (
          <p className="px-2 pb-1 text-[11px] leading-snug text-sidebar-foreground/50">{hint}</p>
        ) : null}
        <SidebarGroupAction
          type="button"
          className={cn(
            'transition-colors hover:bg-sidebar-accent/50',
            groupToneClass,
            expanded ? 'hover:text-sidebar-foreground/70' : 'hover:text-sidebar-foreground/50',
          )}
          onClick={() => toggleGroupVisibility(groupKey)}
          aria-label={expanded ? `Hide ${label} pages` : `Show ${label} pages`}
          title={expanded ? `Hide ${label} pages` : `Show ${label} pages`}
        >
          {expanded ? <Eye /> : <EyeOff />}
        </SidebarGroupAction>
        {expanded && (
          <SidebarGroupContent>
            <SidebarMenu>
              {items?.length ? renderNavItems(items) : null}
              {extraContent}
            </SidebarMenu>
          </SidebarGroupContent>
        )}
      </SidebarGroup>
    );
  };

  const rolesNavExtra = role === 'super_admin' && !viewAsRole && typeof onViewAsRole === 'function'
    ? ROLES_NAV_ITEMS.map(({ key, label, icon: Icon }) => (
        <SidebarMenuItem key={key}>
          <SidebarMenuButton
            onClick={() => { onViewAsRole(key); closeOnPhone(); }}
            tooltip={`View app as ${label}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{label}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))
    : null;

  return (
    <Sidebar collapsible="offcanvas" variant="inset" {...props}>
      <SidebarContent className="min-h-0">
        <button
          type="button"
          onClick={onLogoClick}
          disabled={!onLogoClick}
          className="flex w-full items-center gap-2 border-0 bg-transparent px-4 py-3 text-left appearance-none"
          aria-label="Go to portal"
        >
          <img
            src="/logo.png"
            alt="Advicelink Logo"
            className="h-8 w-auto shrink-0 object-contain"
          />
          <span className="truncate text-2xl font-semibold text-sidebar-foreground">
            Advicelink
          </span>
        </button>
        {NAV_GROUP_SIDEBAR_ORDER.filter((group) => group !== 'Settings').map((group) => (
          group === 'Client'
            ? renderNavGroup('Client', clientGroupLabel || 'Client', groupItems.Client, {
              labelTitle: clientGroupLabel ? `Pages for ${clientGroupLabel}` : undefined,
              hint: clientGroupLabel ? null : 'Select a client in your portal',
            })
            : renderNavGroup(group, group, groupItems[group])
        ))}
        {settingsTab ? (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton isActive={settingsActive} onClick={() => navigate(settingsTab, undefined, { settings: true })}>
                    <Settings className="h-4 w-4 shrink-0" />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        {rolesNavExtra && renderNavGroup('Roles', 'Roles', [], { extraContent: rolesNavExtra })}
      </SidebarContent>
      <SidebarFooter
        className={cn(
          'mt-auto shrink-0 border-t border-sidebar-border',
          viewAsRole && 'bg-amber-500/10 dark:bg-amber-400/10',
        )}
      >
        <div className="flex flex-col gap-2 p-2">
          {viewAsRole ? (
            <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-sm text-sidebar-foreground">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">Viewing as {VIEW_AS_ROLE_LABELS[viewAsRole] || viewAsRole}</span>
              {onExitViewAs ? (
                <button
                  type="button"
                  onClick={onExitViewAs}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium hover:bg-sidebar-accent focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label="Exit view-as mode"
                >
                  <X className="h-3.5 w-3.5" />
                  Exit
                </button>
              ) : null}
            </div>
          ) : null}
          {showSupportChat ? (
            <button
              type="button"
              onClick={() => { requestOpenSupportChat(); closeOnPhone(); }}
              className="flex items-center gap-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground rounded-md py-1.5 w-full justify-start"
            >
              <MessageCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">Support chat</span>
              {supportChatUnread > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-destructive px-1 text-xs font-medium text-destructive-foreground">
                  {formatBadgeCount(supportChatUnread)}
                </span>
              ) : null}
            </button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground rounded-md py-1.5 w-full justify-start"
                aria-label="Theme and appearance"
              >
                <Palette className="h-4 w-4 shrink-0" />
                <span>Theme</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              collisionPadding={8}
              className="w-64 max-h-[calc(var(--radix-dropdown-menu-content-available-height)-8px)] overflow-y-auto"
            >
              <DropdownMenuLabel className="pb-1">Appearance</DropdownMenuLabel>
              <div className="grid grid-cols-2 gap-1 px-1 pb-1">
                {[
                  [THEMES.light, 'Light', Sun],
                  [THEMES.dark, 'Dark', Moon],
                ].map(([value, label, Icon]) => (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => setTheme(value)}
                    aria-checked={theme === value}
                    role="menuitemradio"
                    className={cn(
                      'justify-center gap-1.5 text-xs cursor-pointer border',
                      theme === value ? 'bg-accent text-accent-foreground font-medium border-border' : 'border-transparent',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="pb-1">Color</DropdownMenuLabel>
              <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                {Object.entries(COLOR_THEME_LABELS).map(([value, label]) => (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => setColorTheme(value)}
                    aria-checked={colorTheme === value}
                    role="menuitemradio"
                    className={cn(
                      'justify-center px-1 text-xs cursor-pointer border whitespace-nowrap',
                      colorTheme === value ? 'bg-accent text-accent-foreground font-medium border-border' : 'border-transparent',
                    )}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="pb-1">Sidebar colour</DropdownMenuLabel>
              <SidebarColourPicker />
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-2 text-sm text-sidebar-foreground py-1.5">
            <UserCircle className="h-4 w-4 shrink-0" />
            <span className="truncate">{hasUser ? userDisplay : 'Loading…'}</span>
          </div>
          <button
            type="button"
            onClick={hasUser ? header.handleLogout : undefined}
            disabled={!hasUser}
            className="flex items-center gap-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-foreground disabled:pointer-events-none disabled:opacity-50 py-1.5 w-full justify-start"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

export default AppSidebar;
// Re-export for FinancialAdviserApp to avoid circular dependency (sidebar → … → FinancialAdviserApp)
export { SidebarProvider, SidebarInset, SidebarTrigger };
